import { useState } from 'react';
import { Gift, Copy, Check, Users, Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useReferralMe } from '../hooks/useReferralMe';

export function ReferralCard() {
  const { data, loading } = useReferralMe();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  async function handleCopy() {
    if (!data?.referral_link) return;
    try {
      await navigator.clipboard.writeText(data.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = data.referral_link;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm animate-pulse">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-gray-200 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-gray-200 rounded w-2/3" />
            <div className="h-4 bg-gray-200 rounded w-full" />
          </div>
        </div>
        <div className="h-10 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="h-16 bg-gray-200 rounded" />
          <div className="h-16 bg-gray-200 rounded" />
        </div>
        <div className="h-10 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 border-2 border-blue-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg">
          <Gift size={24} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900 leading-tight">
            Parrainez un autre dirigeant
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            1 parrainage validé = 1 mois offert
          </p>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-1.5 font-medium uppercase tracking-wide">
          Votre lien de parrainage
        </p>
        <div className="flex gap-2">
          <div className="flex-1 min-w-0 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            <span className="text-sm text-gray-600 block truncate font-mono">
              {data?.referral_link || '—'}
            </span>
          </div>
          <button
            onClick={handleCopy}
            disabled={!data?.referral_link}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              copied
                ? 'bg-green-500 text-white'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {copied ? (
              <>
                <Check size={14} />
                <span>Copié</span>
              </>
            ) : (
              <>
                <Copy size={14} />
                <span>Copier</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <Calendar size={14} className="text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Mois gagnés</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {data?.stats.months_won ?? 0}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <Users size={14} className="text-green-600" />
            <span className="text-xs font-medium text-green-700">Filleuls abonnés</span>
          </div>
          <p className="text-2xl font-bold text-green-700">
            {data?.stats.paying_referrals ?? 0}
          </p>
        </div>
      </div>

      <button
        onClick={() => navigate('/app/referral')}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 font-medium text-sm rounded-lg transition-colors"
      >
        Voir le programme complet
        <ArrowRight size={14} />
      </button>
    </div>
  );
}
